/** Identifiants des formules affichées dans l’UI (sale). */
export const EARN_POINTS_PRESET_IDS = ["sale.order_total", "sale.product_points"];

/** Presets sale par défaut si ruleHints.earnPointsExamples absent côté API. */
export const DEFAULT_SALE_EARN_POINTS_EXAMPLES = [
    {
        id: "sale.order_total",
        label: "Montant commande (orderTotal)",
        description: "1 point = 1 unité monétaire du montant total de la commande",
        points: {
            $fn: "to_number",
            args: [{ $path: "payload.orderTotal" }],
        },
    },
    {
        id: "sale.product_points",
        label: "Points par produit (catalogue)",
        description: "Somme des points catalogue × quantité pour chaque produit de la commande",
        points: {
            $fn: "sum_product_points_unomi",
            args: [
                { $path: "payload.productNames" },
                { $path: "payload.productQuantities" },
            ],
        },
    },
];

function filterEarnPointsExamples(examples) {
    const allowed = new Set(EARN_POINTS_PRESET_IDS);
    return (examples || []).filter((ex) => allowed.has(ex.id));
}

export function resolveEarnPointsExamples(ruleHints, transactionType) {
    const fromApi = ruleHints?.earnPointsExamples;
    if (Array.isArray(fromApi) && fromApi.length > 0) return filterEarnPointsExamples(fromApi);
    if (transactionType === "sale") return DEFAULT_SALE_EARN_POINTS_EXAMPLES;
    return [];
}

/** Détecte le preset correspondant à une expression points sauvegardée. */
export function matchEarnPointsPreset(pointsExpr, examples) {
    if (!pointsExpr || typeof pointsExpr !== "object") return null;
    const serialized = JSON.stringify(pointsExpr);
    return (examples || []).find((ex) => JSON.stringify(ex.points) === serialized) || null;
}

export function parseDynamicEarnPointsParams(rest, examples = []) {
    const points = rest.points;
    if (!points || typeof points !== "object") {
        return { ...rest, _pointsMode: "fixed" };
    }

    const matched = matchEarnPointsPreset(points, examples);
    if (matched) {
        return {
            ...rest,
            points: "",
            _pointsMode: "dynamic",
            _dynamicPresetId: matched.id,
            _earnPointsExpr: points,
            _dynamicPath: "",
        };
    }

    if (points.$fn === "sum_product_points_unomi") {
        return {
            ...rest,
            points: "",
            _pointsMode: "dynamic",
            _dynamicPresetId: "sale.product_points",
            _earnPointsExpr: points,
            _dynamicPath: "productNames",
        };
    }

    if (points.$path) {
        const displayPath = String(points.$path).replace(/^(transaction\.)?payload\./, "");
        return {
            ...rest,
            points: "",
            _pointsMode: "dynamic",
            _dynamicPath: displayPath,
            _earnPointsExpr: points,
        };
    }

    if (points.$fn) {
        return {
            ...rest,
            points: "",
            _pointsMode: "dynamic",
            _earnPointsExpr: points,
            _showAdvancedExpr: true,
        };
    }

    return { ...rest, _pointsMode: "fixed" };
}

/** Options affichables dans un sélecteur (pas de JSON côté utilisateur). */
export function buildEarnPointsCalcOptions(examples = [], _payloadFields = [], params = null) {
    const opts = [];
    examples.forEach((ex) => {
        opts.push({
            id: `preset:${ex.id}`,
            label: ex.label,
            description: ex.description || "Formule recommandée",
            kind: "preset",
            preset: ex,
        });
    });
    const expr = params?._earnPointsExpr;
    if (expr && typeof expr === "object" && !matchEarnPointsPreset(expr, examples)) {
        opts.unshift({
            id: "legacy:expr",
            label: "Formule existante (configuration avancée)",
            description: "Conserve la règle telle qu’enregistrée — choisissez une autre option pour la remplacer",
            kind: "legacy",
        });
    }
    return opts;
}

export function resolveEarnPointsCalcSelectionId(params, examples = []) {
    if (params._dynamicPresetId) return `preset:${params._dynamicPresetId}`;
    if (params._dynamicPath) return `field:${params._dynamicPath}`;
    const matched = matchEarnPointsPreset(params._earnPointsExpr, examples);
    if (matched) return `preset:${matched.id}`;
    if (params._showAdvancedExpr || (params._earnPointsExpr && typeof params._earnPointsExpr === "object")) {
        return "legacy:expr";
    }
    return "";
}

export function applyEarnPointsCalcOption(option, setParams) {
    if (!option || option.kind === "legacy") return;
    if (option.kind === "preset") {
        setParams({
            _pointsMode: "dynamic",
            _dynamicPresetId: option.preset.id,
            _earnPointsExpr: option.preset.points,
            _dynamicPath: "",
        });
        return;
    }
    const meta = option.meta;
    setParams({
        _pointsMode: "dynamic",
        _dynamicPresetId: "",
        _dynamicPath: option.fieldName,
        _earnPointsExpr: meta?.earnPointsExample || {
            $fn: "to_number",
            args: [{ $path: `payload.${option.fieldName}` }],
        },
    });
}

export function buildDynamicEarnPoints(params, payloadFields = []) {
    if (params._earnPointsExpr && typeof params._earnPointsExpr === "object"
        && Object.keys(params._earnPointsExpr).length > 0) {
        return params._earnPointsExpr;
    }

    if (params._dynamicPresetId) {
        const found = DEFAULT_SALE_EARN_POINTS_EXAMPLES.find((ex) => ex.id === params._dynamicPresetId);
        if (found) return found.points;
    }

    const pathField = (params._dynamicPath || "orderTotal").trim()
        .replace(/^(transaction\.)?payload\./, "");
    const fieldMeta = payloadFields.find((f) => f.name === pathField || f.earnPointsPath === pathField);
    if (fieldMeta?.earnPointsExample) return fieldMeta.earnPointsExample;

    if (params._dynamicPath === "productNames") {
        return DEFAULT_SALE_EARN_POINTS_EXAMPLES.find((ex) => ex.id === "sale.product_points")?.points
            || DEFAULT_SALE_EARN_POINTS_EXAMPLES[1]?.points;
    }

    return {
        $fn: "to_number",
        args: [{ $path: `payload.${pathField || "orderTotal"}` }],
    };
}
